import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import axios from 'axios';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    vat_number: '',
    notes: ''
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Errore nel caricamento clienti:', error);
      showSnackbar('Errore nel caricamento clienti', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name || '',
        contact_person: customer.contact_person || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        vat_number: customer.vat_number || '',
        notes: customer.notes || ''
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        vat_number: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      vat_number: '',
      notes: ''
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      showSnackbar('Il nome del cliente è obbligatorio', 'error');
      return false;
    }
    
    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      showSnackbar('Formato email non valido', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const customerData = {
        name: formData.name.trim(),
        contact_person: formData.contact_person.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim() || null,
        vat_number: formData.vat_number.trim() || null,
        notes: formData.notes.trim() || null
      };

      if (editingCustomer) {
        await axios.put(`/api/customers/${editingCustomer.id}`, customerData);
        showSnackbar('Cliente aggiornato con successo', 'success');
      } else {
        await axios.post('/api/customers', customerData);
        showSnackbar('Cliente creato con successo', 'success');
      }

      handleCloseDialog();
      loadCustomers();
    } catch (error) {
      console.error('Errore nel salvataggio cliente:', error);
      showSnackbar(
        error.response?.data?.message || 'Errore nel salvataggio cliente', 
        'error'
      );
    }
  };

  const handleDelete = async (customerId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo cliente? Questa azione eliminerà anche tutti gli ordini associati.')) {
      try {
        await axios.delete(`/api/customers/${customerId}`);
        showSnackbar('Cliente eliminato con successo', 'success');
        loadCustomers();
      } catch (error) {
        console.error('Errore nell\'eliminazione cliente:', error);
        showSnackbar(
          error.response?.data?.message || 'Errore nell\'eliminazione cliente', 
          'error'
        );
      }
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'Nome Cliente', width: 200 },
    { field: 'contact_person', headerName: 'Referente', width: 150 },
    { 
      field: 'email', 
      headerName: 'Email', 
      width: 200,
      renderCell: (params) => (
        params.value ? (
          <a href={`mailto:${params.value}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            {params.value}
          </a>
        ) : '-'
      )
    },
    { field: 'phone', headerName: 'Telefono', width: 130 },
    { field: 'vat_number', headerName: 'P.IVA', width: 130 },
    {
      field: 'created_at',
      headerName: 'Data Creazione',
      width: 150,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString('it-IT');
      }
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Modifica">
            <IconButton
              size="small"
              color="info"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Anagrafica Clienti
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadCustomers}
            >
              Aggiorna
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nuovo Cliente
            </Button>
          </Box>
        </Box>

        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={customers}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'Nessun cliente trovato',
              footerRowSelected: (count) => `${count} riga${count !== 1 ? 'he' : ''} selezionata${count !== 1 ? 'e' : ''}`,
              footerTotalRows: 'Righe totali:',
              columnMenuSortAsc: 'Ordine crescente',
              columnMenuSortDesc: 'Ordine decrescente',
              columnMenuFilter: 'Filtro',
              columnMenuHideColumn: 'Nascondi colonna',
              columnMenuShowColumns: 'Mostra colonne'
            }}
          />
        </Box>

        {/* Dialog per aggiungere/modificare cliente */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingCustomer ? 'Modifica Cliente' : 'Nuovo Cliente'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Nome Cliente *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Persona di Contatto"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Telefono"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Partita IVA"
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                />
              </Box>

              <TextField
                fullWidth
                label="Indirizzo"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />

              <TextField
                fullWidth
                label="Note"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingCustomer ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar per messaggi */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default CustomerManagement;